
import React from 'react';
import { t } from '@superset-ui/core';
import {
  ControlPanelConfig,
  sections,
  sharedControls,
  ControlStateMapping,
  emitFilterControl,
  ControlPanelsContainerProps,
} from '@superset-ui/chart-controls';
import { DEFAULT_FORM_DATA } from './types';
import { legendSection } from '../../../controls';
import { commonControlsConfig } from '../utils';
import { commonControlsConfig as commonControlsConfigForPieChart } from '../../Pie/utils';
import { DEFAULT_FORM_DATA as DEFAULT_FORM_DATA_For_Pie_Chart } from '../../Pie/BasicPie/types';
import { DrillDownChartType } from '../../types';

// basic

const { showLabels } = DEFAULT_FORM_DATA;

const barLegendSection = [...legendSection];
barLegendSection.splice(2, 1);

const config: ControlPanelConfig = {
  controlPanelSections: [
    sections.legacyRegularTime,
    {
      label: t('Query'),
      expanded: true,
      controlSetRows: [
        [
          {
            name: 'enable_drill_down',
            config: {
              type: 'CheckboxControl',
              label: t('Enable DrillDown'),
              renderTrigger: false,
              default: DEFAULT_FORM_DATA.enableDrillDown,
              description: t('To enable selection of multiple columns for DrillDown Query.'),
            },
          },
        ],

        [
          {
            name: 'groupby',
            config: {
              ...sharedControls.groupby,
              multi: false,
              label: t('X Axis'),
              description: t('Column values to use as x-axis categories.'),
            },
          },
        ],

        [
          {
            name: 'drill_down_groupby',
            config: {
              ...sharedControls.groupby,
              multi: true,
              label: t('X Axis Drilldown Columns'),
              description: t('Column values to use as x-axis categories for each level.'),
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.enable_drill_down?.value),
            },
          },
        ],
        ['metrics'],
        ['adhoc_filters'],
        emitFilterControl,
        [
          {
            name: 'row_limit',
            config: {
              ...sharedControls.row_limit,
              default: 10,
            },
          },
        ]
      ],
    },

    {
      label: t('Chart Options'),
      expanded: true,
      controlSetRows: [
        ['color_scheme'],
        ...barLegendSection,
        // eslint-disable-next-line react/jsx-key
        [<div className="section-header">{t('Labels')}</div>],
        //  [
        //    {
        //      name: 'label_type',
        //      config: {
        //        type: 'SelectControl',
        //        label: t('Label Type'),
        //        default: labelType,
        //        renderTrigger: true,
        //        choices: [
        //          [EchartsFunnelLabelTypeType.Key, 'Category Name'],
        //          [EchartsFunnelLabelTypeType.Value, 'Value'],
        //          [EchartsFunnelLabelTypeType.Percent, 'Percentage'],
        //          [EchartsFunnelLabelTypeType.KeyValue, 'Category and Value'],
        //          [
        //            EchartsFunnelLabelTypeType.KeyPercent,
        //            'Category and Percentage',
        //          ],
        //          [
        //            EchartsFunnelLabelTypeType.KeyValuePercent,
        //            'Category, Value and Percentage',
        //          ],
        //        ],
        //        description: t('What should be shown on the label?'),
        //      },
        //    },
        //  ],
        //  [
        //    {
        //      name: 'number_format',
        //      config: {
        //        type: 'SelectControl',
        //        freeForm: true,
        //        label: t('Number format'),
        //        renderTrigger: true,
        //        default: numberFormat,
        //        choices: D3_FORMAT_OPTIONS,
        //        description: `${t(
        //          'D3 format syntax: https://github.com/d3/d3-format',
        //        )} ${t('Only applies when "Label Type" is set to show values.')}`,
        //      },
        //    },
        //  ],
        [
          {
            name: 'show_labels',
            config: {
              type: 'CheckboxControl',
              label: t('Show Labels'),
              renderTrigger: true,
              default: showLabels,
              description: t('Whether to display the labels.'),
            },
          },
        ],
        [
          {
            name: 'combineMetrics',
            config: {
              ...commonControlsConfig.combineMetrics,
              default: DEFAULT_FORM_DATA.combineMetrics
            }
          }
        ],

        [
          {
            name: 'drillDownChartType',
            config: {
              type: 'SelectControl',
              label: t('DrillDown Chart'),
              default: DrillDownChartType.Bar,
              renderTrigger: true,
              choices: [
                [DrillDownChartType.Bar, 'Bar Chart'],
                [DrillDownChartType.Pie, 'Pie Chart'],
              ],
              description: t('Which chart to use for drill-down query?'),
            },
          }
        ],
        [
          {
            name: 'tooltipFormat',
            config: {
              ...commonControlsConfigForPieChart.tooltipFormat,
              default: DEFAULT_FORM_DATA_For_Pie_Chart.tooltipFormat,
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.drillDownChartType?.value === DrillDownChartType.Pie),
            },
          }
        ],
        [
          {
            name: 'labelPosition',
            config: {
              ...commonControlsConfigForPieChart.labelPosition,
              default: DEFAULT_FORM_DATA_For_Pie_Chart.labelPosition,
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.drillDownChartType?.value === DrillDownChartType.Pie),
            },
          }
        ],
        [
          {
            name: 'labelAlignment',
            config: {
              ...commonControlsConfigForPieChart.labelAlignment,
              default: DEFAULT_FORM_DATA_For_Pie_Chart.labelAlignment,
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.drillDownChartType?.value === DrillDownChartType.Pie),
            },
          }
        ],
        [
          {
            name: 'enableFiltering',
            config: {
              type: 'CheckboxControl',
              label: t('Enable Filtering'),
              renderTrigger: true,
              default: false,
              description: t('Whether to apply a filter on selecting a bar.'),
            },
          },
        ],

      ],
    },
  ],
  onInit(state: ControlStateMapping) {
    return {
      ...state,
      row_limit: {
        ...state.row_limit,
        value: state.row_limit.default,
      },
    };
  },
  denormalizeFormData: formData => ({
    ...formData,
    metric: formData.standardizedFormData.standardizedState.metrics[0],
    groupby: formData.standardizedFormData.standardizedState.columns,
  }),
  updateStandardizedState: (prevState, currState) => ({
    ...currState,
    metrics: [currState.metrics[0], ...prevState.metrics.slice(1)],
  }),
};

export default config;
